import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import cloneDeep from 'lodash.clonedeep';
import xor from 'lodash.xor';
import {NumberInput} from 'src/components/input';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {McpAlertsPagination} from 'src/modules/profile/account/tabs/mcp/components/mcp-alerts-pagination';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import BoxLoading from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';
import {stringToNumber} from '@pb/kit-formatters';

import {AlertsList} from './alerts-list';
import {UsersDropDown} from './users-drop-down';

import {
    mcpAlerts,
    mcpAlertsTable,
    mcpAlertsTableCell,
    mcpAlertsTableRow,
} from './alerts-view.module.styl';

import type {McpContract} from 'src/openapi/admin/models/McpContract';
import type {McpCreditAlertInfo} from 'src/openapi/admin/models/McpCreditAlertInfo';
import type {McpCreditAlertSettings} from 'src/openapi/admin/models/McpCreditAlertSettings';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';

type Props = {
    accountId: number,
    data: McpCreditAlertSettings,
    users: Array<UserSearchResult>,
    contracts: Array<McpContract>,
    alertsBusy: boolean,
    onUpdateAlerts: (settings: McpCreditAlertSettings) => Promise<boolean>,
    onSearchUsers: (query: string) => void,
};

export const AlertsView = (props: Props) => {
    const {accountId, alertsBusy, contracts, onSearchUsers} = props;
    const {locale} = useContext(LocaleContext);
    const {canEditAccountMcp} = useAdminAccessRight();
    const canEdit = canEditAccountMcp(accountId);

    const [creditValue, setCreditValue] = useState('');
    const [overageValue, setOverageValue] = useState('');
    const [userIds, setUserIds] = useState<Array<number>>(props.data.userIds);
    const [activeContractId, setActiveContractId] = useState<number>(0);
    const [alertsInfo, setAlertsInfo] = useState(props.data.alertsInfo);
    const [isChanged, setIsChanged] = useState(false);
    const baselineRef = useRef({alertsInfo: props.data.alertsInfo, userIds: props.data.userIds});

    const [currentPage, setCurrentPage] = useState(1);

    const hasActiveContracts = !!contracts.length;
    const alertsWithData = useMemo(
        () => alertsInfo.filter(
            item => item.creditsBelow.length > 0 || item.overagesBelow.length > 0,
        ),
        [alertsInfo],
    );
    const hasAlerts = alertsWithData.length > 0;
    const isAddDisabled = !activeContractId || (!creditValue && !overageValue);
    const isSaveDisabled = !isChanged;

    const pageSize = 5;
    const paginatedAlerts = useMemo(
        () => alertsWithData.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize,
        ),
        [alertsWithData, currentPage],
    );
    const showPagination = alertsWithData.length > pageSize;

    const onCreditsChange = useCallback((value?: number) => {
        setCreditValue(value ? value.toString() : '');
    }, []);

    const onOveragesChange = useCallback((value?: number) => {
        setOverageValue(value ? value.toString() : '');
    }, []);

    const onContractChange = useCallback(
        (value: number) => setActiveContractId(value),
        [],
    );

    const onRemove = useCallback(
        (contractId: number, value: number, type: 'credits' | 'overages') => {
            const newAlerts = cloneDeep(alertsInfo);
            const alert = newAlerts.find(
                item => item.contractId === contractId,
            );
            if (alert) {
                if (type === 'credits') {
                    alert.creditsBelow = alert.creditsBelow.filter(
                        v => v !== value,
                    );
                } else {
                    alert.overagesBelow = alert.overagesBelow.filter(
                        v => v !== value,
                    );
                }
                setAlertsInfo(newAlerts);
                setCurrentPage(1);
            }
        },
        [alertsInfo],
    );

    const onSave = async () => {
        const success = await props.onUpdateAlerts({
            alertsInfo,
            userIds: userIds.map(Number),
        });
        if (success) {
            baselineRef.current = {alertsInfo, userIds};
            setIsChanged(false);
        }
    };

    const onAdd = useCallback(() => {
        const newAlerts = cloneDeep(alertsInfo);
        const idx = newAlerts.findIndex(
            ({contractId}) => activeContractId === contractId,
        );
        const credits = creditValue
            ? stringToNumber(creditValue, locale)
            : 0;
        const overages = overageValue
            ? stringToNumber(overageValue, locale)
            : 0;


        if (idx === -1) {
            const newEntry: McpCreditAlertInfo = {
                contractId: activeContractId,
                creditsBelow: credits > 0 ? [credits] : [],
                overagesBelow: overages > 0 ? [overages] : [],
            };
            newAlerts.push(newEntry);
        } else {
            if (credits > 0 && !newAlerts[idx].creditsBelow.includes(credits)) {
                newAlerts[idx].creditsBelow.push(credits);
            }
            if (overages > 0
                && !newAlerts[idx].overagesBelow.includes(overages)) {
                newAlerts[idx].overagesBelow.push(overages);
            }
        }

        setActiveContractId(0);
        setCreditValue('');
        setOverageValue('');
        setAlertsInfo(newAlerts);
        setCurrentPage(1);
    }, [alertsInfo, creditValue, overageValue, activeContractId, locale]);

    useEffect(() => {
        const usersChanged = !!xor(baselineRef.current.userIds, userIds).length;
        const alertsChanged = !isDeepEqual({
            prev: baselineRef.current.alertsInfo,
            current: alertsInfo,
        });
        const isDataChanged = usersChanged || alertsChanged;

        if (isDataChanged) {
            setIsChanged(true);
        } else if (!isDataChanged && isChanged) {
            setIsChanged(false);
        }
    }, [alertsInfo, userIds, isChanged]);

    return (
        <BoxLoading loading={alertsBusy}>
            <div style={{padding: '24px'}}>
                <Group direction="v" gap="l" alignItems="start">
                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                        Alerts
                    </Typography>
                    <div style={{width: 500}}>
                        <Group direction="v" gap="l">
                            <Group className={mcpAlerts} direction="v" gap="l">
                                <Item>
                                    <Label>Alerts set up for</Label>
                                    <UsersDropDown
                                        users={props.users}
                                        checkedIds={userIds}
                                        disabled={!hasActiveContracts || !canEdit}
                                        onChange={setUserIds}
                                        onSearch={onSearchUsers}
                                    />
                                </Item>
                                <div className={mcpAlertsTable}>
                                    <div className={mcpAlertsTableRow}>
                                        <div className={mcpAlertsTableCell}>
                                            <Label>Alert when</Label>
                                        </div>
                                        <div className={mcpAlertsTableCell}>
                                            <Label>credits below</Label>
                                        </div>
                                        <div className={mcpAlertsTableCell}>
                                            <Label>overages below</Label>
                                        </div>
                                    </div>
                                    {hasAlerts ? (
                                        <AlertsList
                                            data={paginatedAlerts}
                                            contracts={contracts}
                                            onRemove={onRemove}
                                            disabled={!canEdit}
                                        />
                                    ) : null}
                                    <div className={mcpAlertsTableRow}>
                                        <div className={mcpAlertsTableCell}>
                                            <div style={{
                                                width: hasActiveContracts
                                                    ? 186
                                                    : 240,
                                            }}>
                                                <ComboBox
                                                    placeholder={
                                                        hasActiveContracts
                                                            ? 'Contract #'
                                                            : 'No active contracts'
                                                    }
                                                    onChange={onContractChange}
                                                    disabled={
                                                        !hasActiveContracts
                                                        || !canEdit
                                                    }
                                                    value={activeContractId}>
                                                    {contracts.map(
                                                        ({
                                                            contractId,
                                                            contractNumber,
                                                        }) => (
                                                            <ComboboxItem
                                                                key={contractId}
                                                                value={contractId}
                                                                caption={contractNumber}
                                                            />
                                                        ),
                                                    )}
                                                </ComboBox>
                                            </div>
                                        </div>
                                        <div className={mcpAlertsTableCell}>
                                            <NumberInput
                                                width={148}
                                                value={
                                                    creditValue
                                                        ? +creditValue
                                                        : undefined
                                                }
                                                onBlur={onCreditsChange}
                                                readOnly={!canEdit}
                                                disabled={!hasActiveContracts}
                                                maxValue={
                                                    MAX_CONTRACT_FIELD_VALUE
                                                }
                                                placeholder="# of credits"
                                                displayedValue={
                                                    creditValue
                                                        ? Integer.format(
                                                                +creditValue,
                                                                locale,
                                                            )
                                                        : ''
                                                }
                                            />
                                        </div>
                                        <div className={mcpAlertsTableCell}>
                                            <NumberInput
                                                width={148}
                                                value={
                                                    overageValue
                                                        ? +overageValue
                                                        : undefined
                                                }
                                                onBlur={onOveragesChange}
                                                readOnly={!canEdit}
                                                disabled={!hasActiveContracts}
                                                maxValue={
                                                    MAX_CONTRACT_FIELD_VALUE
                                                }
                                                placeholder="# of overages"
                                                displayedValue={
                                                    overageValue
                                                        ? Integer.format(
                                                                +overageValue,
                                                                locale,
                                                            )
                                                        : ''
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Group>
                            {showPagination ? (
                                <McpAlertsPagination
                                    totalCount={alertsWithData.length}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            ) : null}
                            <Group justify="space-between">
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={isAddDisabled || !canEdit}
                                    onClick={onAdd}>
                                    Add
                                </Button>
                                <Button
                                    variant="Type.PRIMARY"
                                    disabled={isSaveDisabled || !canEdit}
                                    onClick={onSave}>
                                    Save
                                </Button>
                            </Group>
                        </Group>
                    </div>
                </Group>
            </div>
        </BoxLoading>
    );
};
